/* eslint-disable react-hooks/exhaustive-deps */

import type { FC } from 'react';
import React, { forwardRef, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { useLoadScript } from '@react-google-maps/api';
import Geocode from 'react-geocode';
import { useTranslation } from 'react-i18next';
import { geocodeByAddress } from 'react-places-autocomplete';

import { EyeOpenIcon, LocationTargetIcon, MapSecondIcon } from '@setup/svg-icons/svg-icons';
import { MuiLocationInput, MuiPreloader, MuiTooltip } from '@shared/ui-kit';
import { CustomMap } from 'shared/ui/google-map';

import type { Libraries } from '@react-google-maps/api';
import type { MuiLocationInputProps } from '@shared/ui-kit/inputs/LocationInput/types';

Geocode.setApiKey('AIzaSyCFDzfyH7KIIUvgueWOD86mHPxt8uLlQ_c');
Geocode.setLanguage('en');

const libraries: Libraries = ['drawing', 'places', 'visualization'];

export const LocationInput: FC<MuiLocationInputProps> = forwardRef<HTMLHeadingElement, MuiLocationInputProps>(
  (props, ref) => {
    const {
      onChange,
      name,
      value,
      placeholder,
      label,
      isError,
      errorMessage,
      inputVariant,
      customLocationIconContainerMargin,
      ...args
    } = props;

    const { t } = useTranslation();

    const { isLoaded } = useLoadScript({
      googleMapsApiKey: 'AIzaSyCFDzfyH7KIIUvgueWOD86mHPxt8uLlQ_c',
      libraries,
      language: 'en',
    });
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const [isDisabledGetCurrentLocation, setIsDisabledGetCurrentLocation] = useState<boolean>(false);
    const [isUsedCurrentLocation, setIsUsedCurrentLocation] = useState<boolean>(false);
    const [isShowMap, toggleShowMap] = useState(true);

    const handleOpenInGoogleMaps = () => {
      window.open(`http://maps.google.com/maps?q=loc:${value?.map?.lat},${value?.map?.lng}`);
    };
    const onSearchError = () => {
      onChange({ address: '', map: {} });
    };
    const handleChangeInput = (e: string) => {
      setIsUsedCurrentLocation(false);

      if (e?.length === 0) {
        onChange({ address: e, map: {} });
      } else {
        onChange({ ...value, address: e });
      }
    };

    const getLocation = (lat?: number, lng?: number) => {
      Geocode.fromLatLng(String(lat), String(lng)).then((response) => {
        const selectedAddress = response?.results[0]?.formatted_address;
        onChange({ address: selectedAddress, map: { lat, lng } });
      });
    };

    const handleSelect = (address: string) => {
      geocodeByAddress(address).then((response) => {
        if (
          response &&
          response?.length === 1 &&
          response[0] &&
          response[0]?.geometry &&
          response[0]?.geometry?.location
        ) {
          onChange({
            address,
            map: {
              lat: response[0]?.geometry?.location.lat(),
              lng: response[0]?.geometry?.location.lng(),
            },
          });
        } else {
          onChange({ address: '', map: {} });
        }
      });
    };

    const handleSetCurrentLocation = () => {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          getLocation(latitude, longitude);
          setLoading(false);
          setIsUsedCurrentLocation(true);
        },
        () => {
          setLoading(false);
          setIsDisabledGetCurrentLocation(true);
        },
      );
    };

    const locationIcon = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          svg: {
            height: '16px',
            width: '16px',
            path: {
              fill: theme.palette.case.aquamarine.a700,
            },
          },
        }}
      >
        <LocationTargetIcon />
      </Box>
    );

    return (
      <Box>
        {isLoaded && (
          <Box>
            <MuiLocationInput
              {...args}
              value={value?.address || ''}
              name={name}
              inputVariant={inputVariant}
              onChange={(e) => handleChangeInput(e)}
              handleSelect={handleSelect}
              onSearchError={onSearchError}
              placeholder={placeholder}
              label={label}
              isError={isError}
              errorMessage={errorMessage}
              ref={ref}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                m: customLocationIconContainerMargin ? customLocationIconContainerMargin : '8px 0 0 0',
              }}
            >
              {isDisabledGetCurrentLocation || isUsedCurrentLocation ? (
                <Box
                  sx={{
                    width: 'content-width',
                    display: 'flex',
                    alignItems: 'center',
                    svg: {
                      path: {
                        fill: theme.palette.case.neutral.n400,
                      },
                    },
                  }}
                >
                  <MuiTooltip
                    color="dark"
                    title={
                      isDisabledGetCurrentLocation
                        ? t('general.tooltips.cantAccessYourLocation')
                        : t('general.tooltips.isAlreadyUsed')
                    }
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {loading ? <MuiPreloader size="small" isShow /> : locationIcon}

                      <Typography ml="8px" variant="t14r" color={theme.palette.case.neutral.n400}>
                        {t('general.tooltips.useMyCurrentLocation')}
                      </Typography>
                    </Box>
                  </MuiTooltip>
                </Box>
              ) : (
                <Box
                  sx={{
                    cursor: loading ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    svg: {
                      path: {
                        fill: theme.palette.case.neutral.n500,
                      },
                    },
                  }}
                  onClick={() => (loading ? true : handleSetCurrentLocation())}
                >
                  {loading ? <MuiPreloader size="small" isShow /> : locationIcon}

                  <Typography ml="8px" variant="t14r" color={theme.palette.case.neutral.n600}>
                    {t('general.tooltips.useMyCurrentLocation')}
                  </Typography>
                </Box>
              )}
              {value?.map?.lat && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      ml: '6px',
                      '&: hover': { opacity: '0.7' },
                      cursor: 'pointer',
                      svg: { width: '16px', height: '16px' },
                    }}
                    onClick={() => handleOpenInGoogleMaps()}
                  >
                    <MuiTooltip color="dark" title={t('general.tooltips.openInGoogleMaps')}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          svg: {
                            height: '16px',
                            width: '16px',
                          },
                        }}
                      >
                        <MapSecondIcon />
                      </Box>
                    </MuiTooltip>
                  </Box>{' '}
                  <Box
                    sx={{
                      ml: '6px',
                      '&: hover': { opacity: '0.7' },
                      cursor: 'pointer',
                      svg: { width: '16px', height: '16px' },
                    }}
                    onClick={() => toggleShowMap(!isShowMap)}
                  >
                    <MuiTooltip
                      color="dark"
                      title={isShowMap ? t('general.tooltips.hideMap') : t('general.tooltips.showMap')}
                    >
                      <Box
                        sx={{
                          width: 'fit-content',
                          svg: {
                            height: '16px',
                            width: '16px',
                          },
                        }}
                      >
                        <EyeOpenIcon />
                      </Box>
                    </MuiTooltip>
                  </Box>
                </Box>
              )}
            </Box>

            {isShowMap && value?.map?.lat && value?.map?.lng && (
              <CustomMap
                isDraggableMarker
                location={{ ...value.map } as google.maps.LatLngLiteral}
                getLocation={(lat, lng) => getLocation(lat, lng)}
              />
            )}
          </Box>
        )}
      </Box>
    );
  },
);
